import { createSlice } from '@reduxjs/toolkit';
import {
  SAVE_REVIEW,
  SUBMIT_RECOMMENDATION,
  NOTIFY_ADMIN,
  CLEAR_NOTIFY_ADMIN,
  RESTART_APPLICATION,
  OVERRIDE_RECOMMENDATION,
  CREATE_TRAINING,
  GET_TRAINING_CONFIG,
  UPDATE_PRIORITY,
  HOLD_FOR_EXPORT,
  HOLD_FOR_ADMIN,
  UNASSIGN_TRAINEE,
  EDIT_TRAINING,
} from '../../../actions-index';
import { ERROR_CONSTS } from '../../../util/constants';

const initialState = {
  status: '',
  alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
};

// ---------------------------------------------------------------------------
// submitDetailsReducer slice
// ---------------------------------------------------------------------------
const submitDetailsSlice = createSlice({
  name: 'submitDetailsReducer',
  initialState: {
    saveReview: { ...initialState, type: SAVE_REVIEW },
    submitRecommendation: { ...initialState, type: SUBMIT_RECOMMENDATION },
  },
  reducers: {
    saveReview(state, action) {
      state.saveReview = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: SAVE_REVIEW,
      };
    },
    submitRecommendation(state, action) {
      state.submitRecommendation = {
        status: action.payload.status,
        alert: action.payload.alert,
        context: action.payload.context,
        type: SUBMIT_RECOMMENDATION,
      };
    },
    resetSubmitRecommendation(state) {
      state.submitRecommendation = { ...initialState, type: SUBMIT_RECOMMENDATION };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SAVE_REVIEW, (state, action) => {
        state.saveReview = {
          status: action.payload.status,
          alert: action.payload.alert,
          type: SAVE_REVIEW,
        };
      })
      .addCase(SUBMIT_RECOMMENDATION, (state, action) => {
        state.submitRecommendation = {
          status: action.payload.status,
          alert: action.payload.alert,
          context: action.payload.context,
          type: SUBMIT_RECOMMENDATION,
        };
      });
  },
});

export const { saveReview, submitRecommendation, resetSubmitRecommendation } = submitDetailsSlice.actions;
export const submitDetailsReducer = submitDetailsSlice.reducer;

// ---------------------------------------------------------------------------
// notifyAdminReducer slice
// ---------------------------------------------------------------------------
const notifyAdminSlice = createSlice({
  name: 'notifyAdminReducer',
  initialState: {
    notifyAdmin: { ...initialState, disabled: true, type: NOTIFY_ADMIN },
  },
  reducers: {
    notifyAdmin(state, action) {
      state.notifyAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        disabled: action.payload.disabled,
        type: NOTIFY_ADMIN,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(NOTIFY_ADMIN, (state, action) => {
      state.notifyAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        disabled: action.payload.disabled,
        type: NOTIFY_ADMIN,
      };
    });
  },
});

export const { notifyAdmin } = notifyAdminSlice.actions;
export const notifyAdminReducer = notifyAdminSlice.reducer;

// ---------------------------------------------------------------------------
// clearNotifyAdminReducer slice
// ---------------------------------------------------------------------------
const clearNotifyAdminSlice = createSlice({
  name: 'clearNotifyAdminReducer',
  initialState: {
    clearNotifyAdmin: { ...initialState, type: CLEAR_NOTIFY_ADMIN },
  },
  reducers: {
    clearNotifyAdmin(state, action) {
      state.clearNotifyAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: CLEAR_NOTIFY_ADMIN,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(CLEAR_NOTIFY_ADMIN, (state, action) => {
      state.clearNotifyAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: CLEAR_NOTIFY_ADMIN,
      };
    });
  },
});

export const { clearNotifyAdmin } = clearNotifyAdminSlice.actions;
export const clearNotifyAdminReducer = clearNotifyAdminSlice.reducer;

// ---------------------------------------------------------------------------
// restartApplicationReducer slice
// ---------------------------------------------------------------------------
const restartApplicationSlice = createSlice({
  name: 'restartApplicationReducer',
  initialState: {
    restartApplication: { ...initialState, type: RESTART_APPLICATION },
  },
  reducers: {
    restartApplication(state, action) {
      state.restartApplication = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: RESTART_APPLICATION,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(RESTART_APPLICATION, (state, action) => {
      state.restartApplication = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: RESTART_APPLICATION,
      };
    });
  },
});

export const { restartApplication } = restartApplicationSlice.actions;
export const restartApplicationReducer = restartApplicationSlice.reducer;

// ---------------------------------------------------------------------------
// overrideRecommendationReducer slice
// ---------------------------------------------------------------------------
const overrideRecommendationSlice = createSlice({
  name: 'overrideRecommendationReducer',
  initialState: {
    overrideRecommendation: { ...initialState, type: OVERRIDE_RECOMMENDATION },
  },
  reducers: {
    overrideRecommendation(state, action) {
      state.overrideRecommendation = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: OVERRIDE_RECOMMENDATION,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(OVERRIDE_RECOMMENDATION, (state, action) => {
      state.overrideRecommendation = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: OVERRIDE_RECOMMENDATION,
      };
    });
  },
});

export const { overrideRecommendation } = overrideRecommendationSlice.actions;
export const overrideRecommendationReducer = overrideRecommendationSlice.reducer;

// ---------------------------------------------------------------------------
// createTrainingReducer slice
// ---------------------------------------------------------------------------
const createTrainingSlice = createSlice({
  name: 'createTrainingReducer',
  initialState: {
    createTraining: {
      ...initialState,
      expectedRecommendation: '',
      emplids: [],
      expJunior: true,
      expIntermediate: true,
      expAdvanced: true,
      expSenior: true,
      jobTypeSeasonal: true,
      jobTypeRegular: true,
      allReviewers: true,
      trainingExists: false,
      recommendationOptions: [],
      decision: '',
      type: CREATE_TRAINING,
    },
  },
  reducers: {
    createTraining(state, action) {
      state.createTraining = {
        status: action.payload.status,
        alert: action.payload.alert,
        expectedRecommendation: action.payload.expectedRecommendation ?? state.createTraining.expectedRecommendation,
        emplids: action.payload.emplids ?? state.createTraining.emplids,
        expJunior: action.payload.expJunior ?? state.createTraining.expJunior,
        expIntermediate: action.payload.expIntermediate ?? state.createTraining.expIntermediate,
        expAdvanced: action.payload.expAdvanced ?? state.createTraining.expAdvanced,
        expSenior: action.payload.expSenior ?? state.createTraining.expSenior,
        jobTypeSeasonal: action.payload.jobTypeSeasonal ?? state.createTraining.jobTypeSeasonal,
        jobTypeRegular: action.payload.jobTypeRegular ?? state.createTraining.jobTypeRegular,
        allReviewers: action.payload.allReviewers ?? state.createTraining.allReviewers,
        decision: action.payload.decision ?? state.createTraining.decision,
        trainingExists: state.createTraining?.trainingExists ?? false,
        recommendationOptions: state.createTraining?.recommendationOptions ?? [],
        type: CREATE_TRAINING,
      };
    },
    getTrainingConfig(state, action) {
      state.createTraining = {
        status: action.payload.status ?? state.createTraining?.alert,
        ...(state.createTraining ?? {}),
        alert: action.payload.alert ?? state.createTraining?.alert,
        trainingExists: action.payload?.trainingExists,
        recommendationOptions: action.payload?.options,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(CREATE_TRAINING, (state, action) => {
        state.createTraining = {
          status: action.payload.status,
          alert: action.payload.alert,
          expectedRecommendation: action.payload.expectedRecommendation ?? state.createTraining.expectedRecommendation,
          emplids: action.payload.emplids ?? state.createTraining.emplids,
          expJunior: action.payload.expJunior ?? state.createTraining.expJunior,
          expIntermediate: action.payload.expIntermediate ?? state.createTraining.expIntermediate,
          expAdvanced: action.payload.expAdvanced ?? state.createTraining.expAdvanced,
          expSenior: action.payload.expSenior ?? state.createTraining.expSenior,
          jobTypeSeasonal: action.payload.jobTypeSeasonal ?? state.createTraining.jobTypeSeasonal,
          jobTypeRegular: action.payload.jobTypeRegular ?? state.createTraining.jobTypeRegular,
          allReviewers: action.payload.allReviewers ?? state.createTraining.allReviewers,
          decision: action.payload.decision ?? state.createTraining.decision,
          trainingExists: state.createTraining?.trainingExists ?? false,
          recommendationOptions: state.createTraining?.recommendationOptions ?? [],
          type: CREATE_TRAINING,
        };
      })
      .addCase(GET_TRAINING_CONFIG, (state, action) => {
        state.createTraining = {
          status: action.payload.status ?? state.createTraining?.alert,
          ...(state.createTraining ?? {}),
          alert: action.payload.alert ?? state.createTraining?.alert,
          trainingExists: action.payload?.trainingExists,
          recommendationOptions: action.payload?.options,
        };
      });
  },
});

export const { createTraining, getTrainingConfig } = createTrainingSlice.actions;
export const createTrainingReducer = createTrainingSlice.reducer;

// ---------------------------------------------------------------------------
// updatePriorityReducer slice
// ---------------------------------------------------------------------------
const updatePrioritySlice = createSlice({
  name: 'updatePriorityReducer',
  initialState: {
    updatePriority: { ...initialState, type: UPDATE_PRIORITY },
  },
  reducers: {
    updatePriority(state, action) {
      state.updatePriority = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: UPDATE_PRIORITY,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(UPDATE_PRIORITY, (state, action) => {
      state.updatePriority = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: UPDATE_PRIORITY,
      };
    });
  },
});

export const { updatePriority } = updatePrioritySlice.actions;
export const updatePriorityReducer = updatePrioritySlice.reducer;

// ---------------------------------------------------------------------------
// toggleHoldForExportReducer slice
// ---------------------------------------------------------------------------
const toggleHoldForExportSlice = createSlice({
  name: 'toggleHoldForExportReducer',
  initialState: {
    toggleHoldForExport: { ...initialState, type: HOLD_FOR_EXPORT },
  },
  reducers: {
    toggleHoldForExport(state, action) {
      state.toggleHoldForExport = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: HOLD_FOR_EXPORT,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HOLD_FOR_EXPORT, (state, action) => {
      state.toggleHoldForExport = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: HOLD_FOR_EXPORT,
      };
    });
  },
});

export const { toggleHoldForExport } = toggleHoldForExportSlice.actions;
export const toggleHoldForExportReducer = toggleHoldForExportSlice.reducer;

// ---------------------------------------------------------------------------
// toggleHoldForAdminReducer slice
// ---------------------------------------------------------------------------
const toggleHoldForAdminSlice = createSlice({
  name: 'toggleHoldForAdminReducer',
  initialState: {
    toggleHoldForAdmin: { ...initialState, type: HOLD_FOR_ADMIN },
  },
  reducers: {
    toggleHoldForAdmin(state, action) {
      state.toggleHoldForAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: HOLD_FOR_ADMIN,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HOLD_FOR_ADMIN, (state, action) => {
      state.toggleHoldForAdmin = {
        status: action.payload.status,
        alert: action.payload.alert,
        type: HOLD_FOR_ADMIN,
      };
    });
  },
});

export const { toggleHoldForAdmin } = toggleHoldForAdminSlice.actions;
export const toggleHoldForAdminReducer = toggleHoldForAdminSlice.reducer;

// ---------------------------------------------------------------------------
// unassignTraineeReducer slice
// ---------------------------------------------------------------------------
const unassignTraineeSlice = createSlice({
  name: 'unassignTraineeReducer',
  initialState: {
    unassignTrainee: {
      ...initialState,
      emplids: [],
      type: UNASSIGN_TRAINEE,
    },
  },
  reducers: {
    unassignTrainee(state, action) {
      state.unassignTrainee = {
        status: action.payload.status ?? state.unassignTrainee.status,
        alert: action.payload.alert ?? state.unassignTrainee.alert,
        emplids: action.payload.emplids ?? [],
        type: UNASSIGN_TRAINEE,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(UNASSIGN_TRAINEE, (state, action) => {
      state.unassignTrainee = {
        status: action.payload.status ?? state.unassignTrainee.status,
        alert: action.payload.alert ?? state.unassignTrainee.alert,
        emplids: action.payload.emplids ?? [],
        type: UNASSIGN_TRAINEE,
      };
    });
  },
});

export const { unassignTrainee } = unassignTraineeSlice.actions;
export const unassignTraineeReducer = unassignTraineeSlice.reducer;

// ---------------------------------------------------------------------------
// editTrainingReducer slice
// ---------------------------------------------------------------------------
const editTrainingSlice = createSlice({
  name: 'editTrainingReducer',
  initialState: {
    editTraining: {
      ...initialState,
      type: EDIT_TRAINING,
    },
  },
  reducers: {
    editTraining(state, action) {
      state.editTraining = {
        ...state.editTraining,
        status: action.payload.status ?? state.editTraining.status,
        alert: action.payload.alert ?? state.editTraining.alert,
        expectedRecommendation: action.payload.expectedRecommendation ?? state.editTraining.expectedRecommendation,
        previousExpectedRecommendation: action.payload.previousExpectedRecommendation ?? state.editTraining.previousExpectedRecommendation,
        type: EDIT_TRAINING,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(EDIT_TRAINING, (state, action) => {
      state.editTraining = {
        ...state.editTraining,
        status: action.payload.status ?? state.editTraining.status,
        alert: action.payload.alert ?? state.editTraining.alert,
        expectedRecommendation: action.payload.expectedRecommendation ?? state.editTraining.expectedRecommendation,
        previousExpectedRecommendation: action.payload.previousExpectedRecommendation ?? state.editTraining.previousExpectedRecommendation,
        type: EDIT_TRAINING,
      };
    });
  },
});

export const { editTraining } = editTrainingSlice.actions;
export const editTrainingReducer = editTrainingSlice.reducer;
